import type { PropsWithChildren } from 'react';
import { createContext, useContext, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import {
	dokonciAutorizaci,
	form602SubmissionDeleteQuery,
	useAxiosInstance,
	usePoMutation,
} from '@gov-nx/api';
import { useMessageEvents } from '@gov-nx/core/events';
import { useLocale } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import { is, isCallable, isNonEmptyString } from '@gov-nx/core/types';
import { pageRouteResolver } from '@gov-nx/web';
import type { AutorizaceContextType } from './context.types';
import { useBoolean } from '@gov-nx/utils/common';
import { useZahajAutorizaciQuery } from './hooks/useZahajAutorizaciQuery';
import { useConfirmAuthCode } from './hooks/useConfirmAuthCode';
import { useSendAuthCodeQuery } from './hooks/useSendAuthCodeQuery';
import {
	AUTHORIZATION_CANCELLED_RETURN_URL_KEY,
	AUTHORIZATION_ID_STORAGE_KEY,
} from './constants';
import { PageCode } from '@gov-nx/module/page';
import type { AuthorizationType } from './types';
import { isHighAuthorization, useAutorizaceStore } from '@gov-nx/store';
import { useSessionStorage } from '@gov-nx/utils/web';

// TODO: use store
const getAuthorizationId = () => {
	const savedValue = localStorage.getItem(AUTHORIZATION_ID_STORAGE_KEY);
	if (is(savedValue) && !Number.isNaN(savedValue)) {
		return Number(savedValue);
	}

	// TODO: pass null
	return 0;
};

// TODO: use store
const setAuthorizationId = (authorizationId: number) => {
	localStorage.setItem(
		AUTHORIZATION_ID_STORAGE_KEY,
		authorizationId.toString()
	);
};

// TODO: use store
const clearAuthorizationId = () => {
	localStorage.removeItem(AUTHORIZATION_ID_STORAGE_KEY);
};

const dispose = () => {
	clearAuthorizationId();
};

const AutorizaceContext = createContext<Nullable<AutorizaceContextType>>(null);

const code = PageCode['autorizace'];

interface AutorizaceContextProviderProps extends PropsWithChildren {}

export function AutorizaceContextProvider({
	children,
}: AutorizaceContextProviderProps) {
	const { axiosInstance } = useAxiosInstance();
	const queryClient = useQueryClient();
	const { toastMessageSuccess, toastMessageError } = useMessageEvents();
	const { t } = useLocale(code);
	const navigate = useNavigate();
	const location = useLocation();
	const authorizationDialog = useBoolean();
	const [ukonId, setUkonId] = useState<string>('');
	// polozkaAutorizaceId used to start the current authorization; kept so the
	// temporary submission can be cleaned up if the user closes the dialog.
	const [activePolozkaAutorizaceId, setActivePolozkaAutorizaceId] =
		useState<string>('');
	const getServiceAuthorizationType = useAutorizaceStore(
		'getServiceAuthorizationType'
	);
	const cancelledAuthorizationReturnUrl = useSessionStorage(
		AUTHORIZATION_CANCELLED_RETURN_URL_KEY
	);

	const cleanupAuthorization = usePoMutation({
		mutationFn: form602SubmissionDeleteQuery,
		onSuccess: (_data, variables) => {
			// The temporary submission was removed on the BE. Drop its cached detail
			// (and the pending list) so a detail view still open on it reloads to the
			// "no longer exists" state — no full window reload, no stale data. If the
			// detail isn't mounted (the usual case), this is a cheap no-op.
			queryClient.invalidateQueries({
				queryKey: ['form602-submission', variables.formularPodaniId],
			});
			queryClient.invalidateQueries({ queryKey: ['form602-submissions'] });
		},
	});

	const finalizeAuthorization = usePoMutation({
		mutationFn: dokonciAutorizaci,
	});

	// Guards against a late `startAuthorization` result writing the ids / return URL
	// back into storage after the dialog was already closed/cancelled — which would
	// resurrect a cancelled authorization and leak stale ids into the next flow.
	const authorizationActiveRef = useRef(false);

	const startAuthorization = useZahajAutorizaciQuery({
		onSuccess: ({ data }) => {
			if (!authorizationActiveRef.current) {
				// The dialog was already closed/cancelled before this start settled.
				// We must NOT resurrect any local state (the race this guard exists
				// for), but the backend did create an authorization — close it with
				// `dokoncit` so it doesn't leak open. onDialogCloseClick couldn't have
				// finalized it: no autorizaceId was stored yet at close time.
				if (data.autorizaceId) {
					finalizeAuthorization.mutate({
						axiosInstance,
						authorizationId: data.autorizaceId,
					});
				}
				return;
			}
			setAuthorizationId(data.autorizaceId ?? 0);
			setUkonId(data.ukonId ?? '');
			cancelledAuthorizationReturnUrl.set(
				location.pathname + location.search + location.hash
			);
		},
	});

	const confirmAuthCode = useConfirmAuthCode({
		onSuccess: async ({ data }) => {
			if (data.status === 'SPATNY_KOD') {
				toastMessageError(t('dialog.messages.spatny-kod'));
				return;
			}
			authorizationDialog.setFalse();

			dispose();
			toastMessageSuccess(t('dialog.messages.success'));
			navigate(pageRouteResolver('moje-podani'));
		},
	});

	const sendAuthCodeQuery = useSendAuthCodeQuery();

	const onNextStep = (
		authorizationType: AuthorizationType.FCM | AuthorizationType.SMS
	) => {
		sendAuthCodeQuery.mutate({
			axiosInstance,
			authorizationId: getAuthorizationId(),
			body: {
				// TODO: proper fix when auth type is not correct
				kanal: authorizationType,
			},
		});
	};

	const onConfirmSubmit = (data: any) => {
		confirmAuthCode.mutate({
			axiosInstance,
			body: { kod: data.oneTimePassword },
			authorizationId: getAuthorizationId(),
		});
	};

	/**
	 * Single post-submit handoff for every service. The user is pushed into the
	 * authorization flow only when both hold:
	 *  1. the backend returned a `polozkaAutorizaceId` (`x-autorizaceid`), and
	 *  2. the service is configured for high (`VYSOKA`) authorization.
	 *
	 * Any other case — no id, a non-enforced service, or low (`NIZKA`)
	 * authorization — falls through to `onNonHighAuthCallback` (the normal
	 * success path). This is what keeps the runtime consistent with the
	 * `isAuthorizationEnforced` button labels: a stray `x-autorizaceid` on a
	 * service that isn't enforced cannot trigger the authorization redirect.
	 */
	const onInitAuthorization = (
		serviceName: string,
		polozkaAutorizaceId?: string | null,
		onNonHighAuthCallback?: () => void
	) => {
		const nonHighAuthCallback = () => {
			if (isCallable(onNonHighAuthCallback)) {
				onNonHighAuthCallback();
			}
		};

		if (!isNonEmptyString(polozkaAutorizaceId)) {
			nonHighAuthCallback();
			return;
		}

		if (!isHighAuthorization(getServiceAuthorizationType(serviceName))) {
			nonHighAuthCallback();
			return;
		}
		onInitAuthorizationHigh(polozkaAutorizaceId);
	};

	const onInitAuthorizationHigh = (polozkaAutorizaceId: string) => {
		if (!isNonEmptyString(polozkaAutorizaceId)) {
			return;
		}
		authorizationActiveRef.current = true;
		setActivePolozkaAutorizaceId(polozkaAutorizaceId);
		authorizationDialog.setTrue();
		startAuthorization.mutate({
			axiosInstance,
			polozkaAutorizaceId,
		});
	};

	/**
	 * Closing the dialog aborts the authorization the same way a user cancel from
	 * NIA does: finalize the authorization on the BE (`dokoncit`, on every
	 * outcome), clean up local data, and remove the temporary submission (the
	 * request behind the "Zrušit formulář" button), keyed by polozkaAutorizaceId.
	 * The user stays where they are.
	 */
	const onDialogCloseClick = () => {
		// From here on, ignore any still-in-flight startAuthorization result.
		authorizationActiveRef.current = false;
		const authorizationId = getAuthorizationId();
		if (authorizationId) {
			finalizeAuthorization.mutate({ axiosInstance, authorizationId });
		}
		if (isNonEmptyString(activePolozkaAutorizaceId)) {
			cleanupAuthorization.mutate({
				axiosInstance,
				formularPodaniId: activePolozkaAutorizaceId,
			});
		}
		dispose();
		cancelledAuthorizationReturnUrl.remove();
		setActivePolozkaAutorizaceId('');
		authorizationDialog.setFalse();
	};

	return (
		<AutorizaceContext.Provider
			value={{
				onConfirmSubmit,
				code,
				startAuthorization,
				confirmAuthCode,
				sendAuthCodeQuery,
				onInitAuthorization,
				ukonId,
				onNextStep,
				onDialogCloseClick,
				authorizationDialogOpen: authorizationDialog.value,
			}}>
			{children}
		</AutorizaceContext.Provider>
	);
}

export const useAutorizaceContext = () => useContext(AutorizaceContext);
